// @ts-nocheck
/**
 * Enterprise Data Isolation Dashboard
 * Epic 75: School Enterprise Data Isolation
 *
 * Comprehensive dashboard for enterprise administrators to manage
 * multi-tenant data isolation, monitoring, and compliance
 */

import React, { useState, useEffect } from 'react';
import {
  Grid,
  Column,
  Tile,
  DataTable,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableHeader,
  TableBody,
  TableCell,
  Button,
  Tag,
  ProgressIndicator,
  ProgressStep,
  Modal,
  TextInput,
  Select,
  SelectItem,
  Loading,
  InlineNotification,
  Search,
  Pagination,
  OverflowMenu,
  OverflowMenuItem,
  Header,
  HeaderName,
  HeaderNavigation,
  HeaderMenu,
  HeaderMenuItem,
  SideNav,
  SideNavItems,
  SideNavLink,
  Content,
  Theme,
} from '@carbon/react';

import {
  Dashboard,
  Security,
  DataBase,
  Cloud,
  ChartLine,
  Settings,
  Warning,
  CheckmarkFilled,
  ErrorFilled,
  InformationFilled,
  View,
  Edit,
  TrashCan,
  Download,
  Upload,
  Add,
  Filter,
} from '@carbon/icons-react';

// Temporarily disabled - SCSS causing build issues with Carbon font imports
// import './EnterpriseDataDashboard.scss';

// ============================================================================
// INTERFACES AND TYPES
// ============================================================================

interface InstitutionalTenant {
  tenant_id: string;
  tenant_name: string;
  tenant_type: 'school' | 'district' | 'enterprise';
  institution_level: string;
  subscription_type: string;
  isolation_level: 'standard' | 'strict' | 'maximum';
  data_classification: string;
  storage_quota_gb: number;
  user_limit: number;
  is_active: boolean;
  created_at: string;
  tenant_status: 'active' | 'suspended' | 'terminated';
}

interface UsageMetrics {
  tenant_id: string;
  d1_queries_today: number;
  kv_operations_today: number;
  r2_storage_used_gb: number;
  r2_bandwidth_used_gb: number;
  last_activity: string;
  quota_usage_percent: number;
  compliance_score: number;
}

interface SecurityAlert {
  alert_id: string;
  tenant_id: string;
  alert_type: string;
  alert_severity: 'low' | 'medium' | 'high' | 'critical';
  alert_message: string;
  created_at: string;
  resolved: boolean;
}

interface ComplianceReport {
  tenant_id: string;
  ferpa_compliance: boolean;
  coppa_compliance: boolean;
  gdpr_compliance: boolean;
  audit_score: number;
  last_audit_date: string;
  violations_count: number;
}

// ============================================================================
// MAIN DASHBOARD COMPONENT
// ============================================================================

export const EnterpriseDataDashboard: React.FC = () => {
  // State management
  const [tenants, setTenants] = useState<InstitutionalTenant[]>([]);
  const [usageMetrics, setUsageMetrics] = useState<UsageMetrics[]>([]);
  const [securityAlerts, setSecurityAlerts] = useState<SecurityAlert[]>([]);
  const [complianceReports, setComplianceReports] = useState<ComplianceReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedTenant, setSelectedTenant] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState(0); // Changed to number for Carbon Tabs selectedIndex
  const [sideNavExpanded, setSideNavExpanded] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Modal states
  const [showTenantModal, setShowTenantModal] = useState(false);
  const [showConfigModal, setShowConfigModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');

  // ============================================================================
  // DATA FETCHING
  // ============================================================================

  useEffect(() => {
    // Set a timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      setLoading(false);
      setError('Dashboard loading timeout. Please refresh the page.');
    }, 15000); // 15 second timeout

    loadDashboardData().finally(() => {
      clearTimeout(loadingTimeout);
    });

    const interval = setInterval(() => {
      loadDashboardData();
    }, 30000); // Refresh every 30 seconds

    return () => {
      clearInterval(interval);
      clearTimeout(loadingTimeout);
    };
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get auth token from localStorage - check multiple possible keys
      const token =
        localStorage.getItem('access_token') ||
        localStorage.getItem('auth_token') ||
        localStorage.getItem('sigma08_jwt_token') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('token');

      if (!token) {
        setError('Authentication required. Please log in again.');
        setLoading(false);
        return;
      }

      const headers: HeadersInit = {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      };

      // Helper function to fetch with proper error handling
      const fetchWithErrorHandling = async (endpoint: string) => {
        const response = await fetch(endpoint, { headers });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`${endpoint} returned ${response.status}: ${errorText}`);
        }

        return await response.json();
      };

      // Use Promise.allSettled to handle individual failures gracefully
      const results = await Promise.allSettled([
        fetchWithErrorHandling('/api/v1/admin/tenants'),
        fetchWithErrorHandling('/api/v1/admin/usage-metrics'),
        fetchWithErrorHandling('/api/v1/admin/security-alerts'),
        fetchWithErrorHandling('/api/v1/admin/compliance-reports'),
      ]);

      // Extract data from settled promises, using empty arrays for failures
      const tenantsData = results[0].status === 'fulfilled' ? results[0].value : { tenants: [] };
      const metricsData = results[1].status === 'fulfilled' ? results[1].value : { metrics: [] };
      const alertsData = results[2].status === 'fulfilled' ? results[2].value : { alerts: [] };
      const complianceData = results[3].status === 'fulfilled' ? results[3].value : { reports: [] };

      setTenants(tenantsData.tenants || []);
      setUsageMetrics(metricsData.metrics || []);
      setSecurityAlerts(alertsData.alerts || []);
      setComplianceReports(complianceData.reports || []);

      // Log any failures for debugging
      results.forEach((result, index) => {
        if (result.status === 'rejected') {
          const endpoints = ['tenants', 'usage-metrics', 'security-alerts', 'compliance-reports'];
          console.warn(`Failed to load ${endpoints[index]}:`, result.reason);
        }
      });
    } catch (err) {
      console.error('Dashboard data loading error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // ============================================================================
  // DATA PROCESSING
  // ============================================================================

  const filteredTenants = tenants.filter(tenant => {
    const matchesSearch =
      tenant.tenant_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      tenant.tenant_id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === 'all' || tenant.tenant_type === filterType;
    return matchesSearch && matchesFilter;
  });

  const paginatedTenants = filteredTenants.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  const dashboardStats = {
    totalTenants: tenants.length,
    activeTenants: tenants.filter(t => t.is_active).length,
    criticalAlerts: securityAlerts.filter(a => a.alert_severity === 'critical' && !a.resolved).length,
    complianceIssues: complianceReports.filter(r => r.violations_count > 0).length,
    totalStorageGB: usageMetrics.reduce((sum, m) => sum + m.r2_storage_used_gb, 0),
    averageQuotaUsage:
      usageMetrics.length > 0
        ? usageMetrics.reduce((sum, m) => sum + m.quota_usage_percent, 0) / usageMetrics.length
        : 0,
  };

  // ============================================================================
  // EVENT HANDLERS
  // ============================================================================

  const handleCreateTenant = () => {
    setModalMode('create');
    setShowTenantModal(true);
  };

  const handleEditTenant = (tenantId: string) => {
    setSelectedTenant(tenantId);
    setModalMode('edit');
    setShowTenantModal(true);
  };

  const handleDeleteTenant = async (tenantId: string) => {
    if (!confirm('Are you sure you want to delete this tenant? This action cannot be undone.')) {
      return;
    }

    try {
      const token =
        localStorage.getItem('access_token') ||
        localStorage.getItem('auth_token') ||
        localStorage.getItem('sigma08_jwt_token') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`/api/v1/admin/tenants/${tenantId}`, {
        method: 'DELETE',
        headers,
      });

      if (!response.ok) {
        throw new Error('Failed to delete tenant');
      }

      await loadDashboardData();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete tenant');
    }
  };

  const handleExportData = async (tenantId: string) => {
    try {
      const token =
        localStorage.getItem('access_token') ||
        localStorage.getItem('auth_token') ||
        localStorage.getItem('sigma08_jwt_token') ||
        localStorage.getItem('token') ||
        sessionStorage.getItem('token');
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };

      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`/api/v1/admin/tenants/${tenantId}/export`, { headers });
      if (!response.ok) throw new Error('Export failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tenant-${tenantId}-data.json`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Export failed');
    }
  };

  // ============================================================================
  // TABLE CONFIGURATIONS
  // ============================================================================

  const tenantTableHeaders = [
    { key: 'tenant_name', header: 'Tenant Name' },
    { key: 'tenant_type', header: 'Type' },
    { key: 'isolation_level', header: 'Isolation Level' },
    { key: 'subscription_type', header: 'Subscription' },
    { key: 'storage_quota_gb', header: 'Storage Quota (GB)' },
    { key: 'user_limit', header: 'User Limit' },
    { key: 'tenant_status', header: 'Status' },
    { key: 'actions', header: 'Actions' },
  ];

  const alertTableHeaders = [
    { key: 'alert_type', header: 'Type' },
    { key: 'tenant_id', header: 'Tenant' },
    { key: 'alert_severity', header: 'Severity' },
    { key: 'alert_message', header: 'Message' },
    { key: 'created_at', header: 'Created' },
    { key: 'status', header: 'Status' },
  ];

  // ============================================================================
  // UTILITY FUNCTIONS
  // ============================================================================

  const getSeverityTag = (severity: string) => {
    const severityMap = {
      critical: { type: 'red' as const, text: 'Critical' },
      high: { type: 'magenta' as const, text: 'High' },
      medium: { type: 'cyan' as const, text: 'Medium' },
      low: { type: 'gray' as const, text: 'Low' },
    };
    const config = severityMap[severity as keyof typeof severityMap] || severityMap.low;
    return <Tag type={config.type}>{config.text}</Tag>;
  };

  const getStatusTag = (status: string, active?: boolean) => {
    if (status === 'active' && active) {
      return <Tag type="green">Active</Tag>;
    } else if (status === 'suspended') {
      return <Tag type="cyan">Suspended</Tag>;
    } else if (status === 'terminated') {
      return <Tag type="red">Terminated</Tag>;
    }
    return <Tag type="gray">Inactive</Tag>;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatBytes = (bytes: number) => {
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    if (bytes === 0) return '0 B';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round((bytes / Math.pow(1024, i)) * 100) / 100 + ' ' + sizes[i];
  };

  // ============================================================================
  // RENDER FUNCTIONS
  // ============================================================================

  const renderOverviewCards = () => (
    <Grid>
      <Column lg={4} md={4} sm={4}>
        <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--cds-spacing-03)',
              marginBottom: 'var(--cds-spacing-04)',
            }}
          >
            <Dashboard size={32} />
            <h4 className="cds--type-heading-03" style={{ margin: 0 }}>
              Total Tenants
            </h4>
          </div>
          <div className="cds--type-heading-05">{dashboardStats.totalTenants}</div>
          <div
            className="cds--body-short-01"
            style={{ color: 'var(--cds-text-secondary)', marginTop: 'var(--cds-spacing-02)' }}
          >
            {dashboardStats.activeTenants} active
          </div>
        </Tile>
      </Column>
      <Column lg={4} md={4} sm={4}>
        <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--cds-spacing-03)',
              marginBottom: 'var(--cds-spacing-04)',
            }}
          >
            <Security size={32} />
            <h4 className="cds--type-heading-03" style={{ margin: 0 }}>
              Critical Alerts
            </h4>
          </div>
          <div className="cds--type-heading-05" style={{ color: 'var(--cds-support-error)' }}>
            {dashboardStats.criticalAlerts}
          </div>
          <div
            className="cds--body-short-01"
            style={{ color: 'var(--cds-text-secondary)', marginTop: 'var(--cds-spacing-02)' }}
          >
            Requiring attention
          </div>
        </Tile>
      </Column>
      <Column lg={4} md={4} sm={4}>
        <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--cds-spacing-03)',
              marginBottom: 'var(--cds-spacing-04)',
            }}
          >
            <ChartLine size={32} />
            <h4 className="cds--type-heading-03" style={{ margin: 0 }}>
              Compliance Issues
            </h4>
          </div>
          <div className="cds--type-heading-05" style={{ color: 'var(--cds-support-warning)' }}>
            {dashboardStats.complianceIssues}
          </div>
          <div
            className="cds--body-short-01"
            style={{ color: 'var(--cds-text-secondary)', marginTop: 'var(--cds-spacing-02)' }}
          >
            Violations found
          </div>
        </Tile>
      </Column>
      <Column lg={6} md={8} sm={4}>
        <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--cds-spacing-03)',
              marginBottom: 'var(--cds-spacing-04)',
            }}
          >
            <Cloud size={32} />
            <h4 className="cds--type-heading-03" style={{ margin: 0 }}>
              Storage Usage
            </h4>
          </div>
          <div className="cds--type-heading-05">{formatBytes(dashboardStats.totalStorageGB * 1024 * 1024 * 1024)}</div>
          <div
            className="cds--body-short-01"
            style={{ color: 'var(--cds-text-secondary)', marginTop: 'var(--cds-spacing-02)' }}
          >
            {dashboardStats.averageQuotaUsage.toFixed(1)}% average quota usage
          </div>
        </Tile>
      </Column>
      <Column lg={6} md={8} sm={4}>
        <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 'var(--cds-spacing-03)',
              marginBottom: 'var(--cds-spacing-04)',
            }}
          >
            <DataBase size={32} />
            <h4 className="cds--type-heading-03" style={{ margin: 0 }}>
              System Status
            </h4>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--cds-spacing-03)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-03)' }}>
              <CheckmarkFilled size={20} style={{ color: 'var(--cds-support-success)' }} />
              <span className="cds--body-short-01">D1 Database: Operational</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-03)' }}>
              <CheckmarkFilled size={20} style={{ color: 'var(--cds-support-success)' }} />
              <span className="cds--body-short-01">KV Storage: Operational</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-03)' }}>
              <CheckmarkFilled size={20} style={{ color: 'var(--cds-support-success)' }} />
              <span className="cds--body-short-01">R2 Storage: Operational</span>
            </div>
          </div>
        </Tile>
      </Column>
    </Grid>
  );

  const renderTenantsTable = () => (
    <DataTable
      rows={paginatedTenants.map(tenant => ({
        id: tenant.tenant_id,
        tenant_name: tenant.tenant_name,
        tenant_type: tenant.tenant_type,
        isolation_level: tenant.isolation_level,
        subscription_type: tenant.subscription_type,
        storage_quota_gb: tenant.storage_quota_gb,
        user_limit: tenant.user_limit,
        tenant_status: getStatusTag(tenant.tenant_status, tenant.is_active),
        actions: (
          <OverflowMenu
            aria-label={`Actions for tenant ${tenant.tenant_name}`}
            iconDescription={`Actions for ${tenant.tenant_name}`}
          >
            <OverflowMenuItem itemText="View Details" onClick={() => setSelectedTenant(tenant.tenant_id)} />
            <OverflowMenuItem itemText="Edit Configuration" onClick={() => handleEditTenant(tenant.tenant_id)} />
            <OverflowMenuItem itemText="Export Data" onClick={() => handleExportData(tenant.tenant_id)} />
            <OverflowMenuItem
              itemText="Delete Tenant"
              onClick={() => handleDeleteTenant(tenant.tenant_id)}
              hasDivider
              isDelete
            />
          </OverflowMenu>
        ),
      }))}
      headers={tenantTableHeaders}
    >
      {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
        <TableContainer title="Institutional Tenants">
          <div className="table-toolbar">
            <div className="table-search">
              <Search
                placeholder="Search tenants..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
              />
            </div>
            <div className="table-filters">
              <Select
                id="tenant-type-filter"
                labelText=""
                value={filterType}
                onChange={e => setFilterType(e.target.value)}
              >
                <SelectItem value="all" text="All Types" />
                <SelectItem value="school" text="Schools" />
                <SelectItem value="district" text="Districts" />
                <SelectItem value="enterprise" text="Enterprise" />
              </Select>
            </div>
            <Button kind="primary" renderIcon={Add} onClick={handleCreateTenant}>
              Add Tenant
            </Button>
          </div>
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                {headers.map(header => (
                  <TableHeader key={header.key} {...getHeaderProps({ header })}>
                    {header.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.id} {...getRowProps({ row })}>
                  {row.cells.map(cell => (
                    <TableCell key={cell.id}>{cell.value}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <Pagination
            totalItems={filteredTenants.length}
            pageSize={pageSize}
            pageSizes={[10, 20, 50]}
            page={currentPage}
            onChange={({ page, pageSize: newPageSize }) => {
              setCurrentPage(page);
              setPageSize(newPageSize);
            }}
          />
        </TableContainer>
      )}
    </DataTable>
  );

  const renderSecurityAlerts = () => (
    <DataTable
      rows={securityAlerts.slice(0, 10).map(alert => ({
        id: alert.alert_id,
        alert_type: alert.alert_type,
        tenant_id: alert.tenant_id,
        alert_severity: getSeverityTag(alert.alert_severity),
        alert_message: alert.alert_message,
        created_at: formatDate(alert.created_at),
        status: alert.resolved ? <Tag type="green">Resolved</Tag> : <Tag type="red">Active</Tag>,
      }))}
      headers={alertTableHeaders}
    >
      {({ rows, headers, getTableProps, getHeaderProps, getRowProps }) => (
        <TableContainer title="Recent Security Alerts">
          <Table {...getTableProps()}>
            <TableHead>
              <TableRow>
                {headers.map(header => (
                  <TableHeader key={header.key} {...getHeaderProps({ header })}>
                    {header.header}
                  </TableHeader>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(row => (
                <TableRow key={row.id} {...getRowProps({ row })}>
                  {row.cells.map(cell => (
                    <TableCell key={cell.id}>{cell.value}</TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </DataTable>
  );

  // ============================================================================
  // MAIN RENDER
  // ============================================================================

  if (loading) {
    return (
      <div className="dashboard-loading">
        <Loading description="Loading enterprise dashboard..." />
      </div>
    );
  }

  return (
    <Theme theme="g100">
      <div className="enterprise-dashboard">
        <Header aria-label="Enterprise Data Dashboard">
          <HeaderName href="/" prefix="">
            Sigma08 Enterprise Dashboard
          </HeaderName>
          <HeaderNavigation aria-label="Enterprise Dashboard">
            <HeaderMenu aria-label="Administration" menuLinkName="Administration">
              <HeaderMenuItem href="/admin/tenants">Tenant Management</HeaderMenuItem>
              <HeaderMenuItem href="/admin/compliance">Compliance Reports</HeaderMenuItem>
              <HeaderMenuItem href="/admin/security">Security Center</HeaderMenuItem>
            </HeaderMenu>
          </HeaderNavigation>
        </Header>

        <SideNav
          isFixedNav
          expanded={sideNavExpanded}
          onToggle={expanded => setSideNavExpanded(expanded)}
          aria-label="Side navigation"
        >
          <SideNavItems>
            <SideNavLink
              renderIcon={Dashboard}
              href="#overview"
              isActive={activeTab === 0}
              onClick={e => {
                e.preventDefault();
                setActiveTab(0);
              }}
            >
              Overview
            </SideNavLink>
            <SideNavLink
              renderIcon={DataBase}
              href="#tenants"
              isActive={activeTab === 1}
              onClick={e => {
                e.preventDefault();
                setActiveTab(1);
              }}
            >
              Tenant Management
            </SideNavLink>
            <SideNavLink
              renderIcon={Security}
              href="#security"
              isActive={activeTab === 2}
              onClick={e => {
                e.preventDefault();
                setActiveTab(2);
              }}
            >
              Security & Alerts
            </SideNavLink>
            <SideNavLink
              renderIcon={ChartLine}
              href="#compliance"
              isActive={activeTab === 3}
              onClick={e => {
                e.preventDefault();
                setActiveTab(3);
              }}
            >
              Compliance
            </SideNavLink>
            <SideNavLink
              renderIcon={Settings}
              href="#settings"
              isActive={activeTab === 4}
              onClick={e => {
                e.preventDefault();
                setActiveTab(4);
              }}
            >
              Settings
            </SideNavLink>
          </SideNavItems>
        </SideNav>

        <Content className="dashboard-content">
          {error && (
            <InlineNotification
              kind="error"
              title="Error"
              subtitle={error}
              onCloseButtonClick={() => setError(null)}
              className="dashboard-notification"
            />
          )}

          {activeTab === 0 && (
            <div className="tab-content">
              <h2>Enterprise Data Isolation Overview</h2>
              {renderOverviewCards()}
              <div className="overview-tables">
                <Grid>
                  <Column lg={16}>{renderSecurityAlerts()}</Column>
                </Grid>
              </div>
            </div>
          )}

          {activeTab === 1 && (
            <div className="tab-content">
              <h2>Institutional Tenant Management</h2>
              {renderTenantsTable()}
            </div>
          )}

          {activeTab === 2 && (
            <div className="tab-content">
              <h2>Security Center</h2>
              {renderSecurityAlerts()}
            </div>
          )}

          {activeTab === 3 && (
            <div className="tab-content">
              <h2>Compliance Monitoring</h2>
              <div style={{ marginTop: 'var(--cds-spacing-05)' }}>
                <Grid>
                  {complianceReports.map(report => (
                    <Column lg={4} md={8} sm={4} key={report.tenant_id}>
                      <Tile style={{ padding: 'var(--cds-spacing-05)' }}>
                        <h4 className="cds--type-heading-03" style={{ marginBottom: 'var(--cds-spacing-04)' }}>
                          Tenant: {report.tenant_id}
                        </h4>
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: 'var(--cds-spacing-03)',
                            marginBottom: 'var(--cds-spacing-04)',
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-03)' }}>
                            {report.ferpa_compliance ? (
                              <CheckmarkFilled size={20} style={{ color: 'var(--cds-support-success)' }} />
                            ) : (
                              <ErrorFilled size={20} style={{ color: 'var(--cds-support-error)' }} />
                            )}
                            <span className="cds--body-short-01">FERPA Compliant</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-03)' }}>
                            {report.coppa_compliance ? (
                              <CheckmarkFilled size={20} style={{ color: 'var(--cds-support-success)' }} />
                            ) : (
                              <ErrorFilled size={20} style={{ color: 'var(--cds-support-error)' }} />
                            )}
                            <span className="cds--body-short-01">COPPA Compliant</span>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--cds-spacing-03)' }}>
                            {report.gdpr_compliance ? (
                              <CheckmarkFilled size={20} style={{ color: 'var(--cds-support-success)' }} />
                            ) : (
                              <ErrorFilled size={20} style={{ color: 'var(--cds-support-error)' }} />
                            )}
                            <span className="cds--body-short-01">GDPR Compliant</span>
                          </div>
                        </div>
                        <div
                          className="cds--body-short-01"
                          style={{ color: 'var(--cds-text-secondary)', marginBottom: 'var(--cds-spacing-03)' }}
                        >
                          Audit Score: {report.audit_score}/100
                        </div>
                        {report.violations_count > 0 && <Tag type="red">{report.violations_count} violations</Tag>}
                      </Tile>
                    </Column>
                  ))}
                </Grid>
              </div>
            </div>
          )}
        </Content>

        {/* Tenant Management Modal */}
        <Modal
          open={showTenantModal}
          onRequestClose={() => setShowTenantModal(false)}
          modalHeading={modalMode === 'create' ? 'Create New Tenant' : 'Edit Tenant Configuration'}
          primaryButtonText={modalMode === 'create' ? 'Create' : 'Save'}
          secondaryButtonText="Cancel"
          size="lg"
        >
          {/* Modal content would go here */}
          <div className="tenant-modal-content">
            <p>Tenant configuration form would be implemented here...</p>
          </div>
        </Modal>
      </div>
    </Theme>
  );
};

export default EnterpriseDataDashboard;
